const MODULE_CONTENT_KEYS = {
  look: 'look_uid',
  looks: 'look_uids',
  favorite_product: 'favorite_product_uid',
  favorite_products: 'favorite_product_uids',
  locations: 'location_uids',
};

const SINGULAR_ARRAY_CONTENT_KEYS = {
  look: 'look_uids',
  favorite_product: 'favorite_product_uids',
};

const MODULE_TYPE_ALIASES = {
  artists: 'participants',
};

const MEDIA_CONTENT_CONFIG = {
  banner: {
    contentKey: 'banner',
    referenceKey: 'banner_uid',
    fields: ['name', 'image_id', 'image_url', 'url'],
    fallbackField: 'image_id',
  },
  banners: {
    contentKey: 'banner',
    referenceKey: 'banner_uid',
    fields: ['name', 'image_id', 'image_url', 'url'],
    fallbackField: 'image_id',
  },
  video: {
    contentKey: 'video',
    referenceKey: 'video_uid',
    fields: ['name', 'video_url', 'video_file_id', 'video_file_url'],
    fallbackField: 'video_url',
  },
  videos: {
    contentKey: 'video',
    referenceKey: 'video_uid',
    fields: ['name', 'video_url', 'video_file_id', 'video_file_url'],
    fallbackField: 'video_url',
  },
};

const isContentObject = (content) =>
  content !== null && typeof content === 'object' && !Array.isArray(content);

const hasOwnProperty = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

const normalizeModuleType = (moduleType) => String(moduleType || '').toLowerCase();

export const buildPageModuleType = (moduleType) =>
  MODULE_TYPE_ALIASES[normalizeModuleType(moduleType)] || normalizeModuleType(moduleType);

export const buildPageModuleName = (_moduleType, name) => name?.trim() || '';

const pickContentFields = (content, fields) =>
  fields.reduce((payload, field) => {
    if (content[field] !== undefined) {
      return {
        ...payload,
        [field]: content[field],
      };
    }
    return payload;
  }, {});

const buildMediaContentPayload = (content, config) => {
  const { contentKey, referenceKey, fields, fallbackField } = config;

  if (isContentObject(content)) {
    if (hasOwnProperty(content, referenceKey)) {
      return { [referenceKey]: content[referenceKey] };
    }

    if (hasOwnProperty(content, contentKey)) {
      return { [contentKey]: content[contentKey] };
    }

    if (hasOwnProperty(content, 'uid')) {
      return { [referenceKey]: content.uid };
    }

    return { [contentKey]: pickContentFields(content, fields) };
  }

  return { [contentKey]: { [fallbackField]: content } };
};

const normalizeParticipantItem = (item) => {
  if (!isContentObject(item)) return item;

  if (
    item.retailer_uid === null ||
    item.uid?.startsWith('custom-artist') ||
    (item.name && item.picture_id)
  ) {
    return {
      ...(item.name ? { name: item.name } : {}),
      ...(item.picture_id ? { picture_id: item.picture_id } : {}),
      ...(item.description ? { description: item.description } : {}),
    };
  }

  const retailerUid = item.retailer_uid || item.uid;

  return {
    ...(retailerUid ? { retailer_uid: retailerUid } : {}),
    ...(item.description ? { description: item.description } : {}),
  };
};

const buildParticipantsPayload = (content) => {
  if (isContentObject(content) && hasOwnProperty(content, 'participant_uids')) {
    return content;
  }

  const participants = Array.isArray(content) ? content : [content];

  return {
    participant_uids: participants.map(normalizeParticipantItem),
  };
};

export const buildPageModuleContentPayload = (moduleType, content) => {
  const normalizedModuleType = normalizeModuleType(moduleType);

  if (normalizedModuleType === 'events') {
    if (!isContentObject(content)) return {};

    return {
      ...(content.mode ? { mode: content.mode } : {}),
      ...(content.mode !== 'UPCOMING' && Array.isArray(content.event_uids)
        ? { event_uids: content.event_uids }
        : {}),
    };
  }

  if (normalizedModuleType === 'artists' || normalizedModuleType === 'participants') {
    return buildParticipantsPayload(content);
  }

  const contentKey = MODULE_CONTENT_KEYS[normalizedModuleType];

  if (contentKey) {
    if (isContentObject(content) && hasOwnProperty(content, contentKey)) {
      return content;
    }
    if (Array.isArray(content) && SINGULAR_ARRAY_CONTENT_KEYS[normalizedModuleType]) {
      return { [SINGULAR_ARRAY_CONTENT_KEYS[normalizedModuleType]]: content };
    }
    return { [contentKey]: content };
  }

  const mediaContentConfig = MEDIA_CONTENT_CONFIG[normalizedModuleType];

  if (mediaContentConfig) {
    return buildMediaContentPayload(content, mediaContentConfig);
  }

  if (isContentObject(content) && hasOwnProperty(content, 'content')) {
    return content;
  }

  return { content };
};
